import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { DBProject, DBInvoice, DBVoucher } from "@/lib/db-types";

export interface TimeEntry {
  id: string;
  project_id: string;
  user_id: string | null;
  task_description: string | null;
  duration_seconds: number | null;
  budgeted_hours: number | null;
  start_date: string | null;
  status: string | null;
  comment: string | null;
  budget_line_id: string | null;
  /** VIB-671: peker direkte på budget_line_assignments-raden timeføringen
   *  tilhører. Brukes til presis per-oppgave-summering når sub-tasks splitter
   *  en linje i flere assignments. */
  assignment_id: string | null;
  user?: { full_name: string; avatar_url: string | null } | null;
}

export type SplitType = "percent" | "fixed";

export interface ProjectOwnerWithUser {
  user_id: string;
  ownership_pct: number | null;
  split_type: SplitType;
  fixed_amount: number | null;
  // VIB-1146: enheten eieren stammer fra (null for legacy bruker-eiere).
  sales_unit_id: string | null;
  user: {
    full_name: string;
    avatar_url: string | null;
  } | null;
}

export interface BudgetLineAssignmentRow {
  id: string;
  user_id: string;
  hours: number;
  label: string | null;  // VIB-671
  task_status: string;
  completed_at: string | null;
  approved_at?: string | null;
  rejection_reason: string | null;
  estimated_deadline: string | null;
  notes: string | null;
  user?: { full_name: string; avatar_url: string | null } | null;
}

export interface BudgetLineRow {
  id: string;
  project_id: string;
  phase: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  quantity_hours: number | null;
  amount: number | null;
  cost_type: string | null;
  estimated_cost: number | null;
  template_id: string | null;
  template_name: string | null;
  upsell_id: string | null;
  /** VIB-995: true for tilvalg (opsjon fra tilbudet) — egen rubrikk på timetildeling. */
  is_optional: boolean;
  /** VIB-1158: navn på tilvalgs-seksjonen — én rubrikk per option (null/undefined = samlet). */
  option_label?: string | null;
  /** VIB-681: true hvis denne linja ble konvertert fra en fastpris-kostnad
   *  til en timer-oppgave. Brukes til audit/filtrering, ikke til beregning. */
  is_converted_from_cost: boolean;
  /** VIB-789: true for Tilbudsskriving-linja etter auto-tildeling — låser
   *  redigering av timer og tildeling i budsjett-tabben. */
  is_locked: boolean;
  /** VIB-2866: rekkefølge innen fase (fra tilbudet). NULL sorteres sist. */
  sort_order: number | null;
  assignments: BudgetLineAssignmentRow[];
}

/** VIB-997: ett bilag fordeles over flere budsjettlinjer via voucher_allocations.
 *  Brukes til "x bilag · y kr"-indikatoren per budsjettlinje (tidl. VIB-545). */
export interface VoucherAllocationRow {
  budget_line_id: string | null;
  amount: number;
  voucher_id: string;
  voucher_status: string | null;
}

export interface CustomerInfo {
  id: string;
  name: string;
  contact_person: string | null;
  contact_email: string | null;
  /** VIB-2521: PO `InvoiceEmailAddress`. */
  invoice_email_address: string | null;
  invoice_email_cc: string | null;
  contact_phone: string | null;
  organization_number: string | null;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  country_code: string | null;
  /** VIB-2575: null = ikke koblet til PowerOffice, kan ikke faktureres. */
  poweroffice_customer_id: number | null;
}

export interface OfferMetadata {
  overhead_pct: number;
  discount_pct: number;
  discount_amount: number;
  offer_total: number;
  startup_invoice_pct: number | null;
  final_invoice_pct: number | null;
  /** VIB-2086: reise i OH-grunnlaget. false = gammel formel. */
  include_travel_in_overhead: boolean;
}

export interface ProjectDetail {
  project: DBProject;
  customer: CustomerInfo | null;
  owners: ProjectOwnerWithUser[];
  invoices: DBInvoice[];
  vouchers: DBVoucher[];
  voucherAllocations: VoucherAllocationRow[];
  timeEntries: TimeEntry[];
  budgetLines: BudgetLineRow[];
  offerMetadata: OfferMetadata | null;
}

export function useProjectDetail(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project-detail", projectId],
    enabled: !!projectId,
    queryFn: async (): Promise<ProjectDetail> => {
      const [projectRes, ownersRes, invoicesRes, vouchersRes, allocationsRes, timeRes, budgetRes, offerMetaRes] = await Promise.all([
        supabase.from("projects").select("*, customers(id, name, contact_person, contact_email, invoice_email_address, invoice_email_cc, contact_phone, organization_number, address, postal_code, city, country_code, poweroffice_customer_id)").eq("id", projectId!).single(),
        supabase.from("project_owners")
          .select("user_id, ownership_pct, split_type, fixed_amount, sales_unit_id, users(full_name, avatar_url)")
          .eq("project_id", projectId!),
        supabase.from("invoices")
          .select("*")
          .eq("project_id", projectId!),
        supabase.from("vouchers")
          .select("*, voucher_approvals(budget_line_id, decision)")
          .eq("project_id", projectId!)
          .order("created_at", { ascending: false }),
        // VIB-997: allokeringer joinet med bilagets project_id + status. Ett
        // bilag kan fordeles over flere budsjettlinjer, så vi summerer per
        // allocation.budget_line_id i stedet for voucher.budget_line_id.
        supabase.from("voucher_allocations")
          .select("budget_line_id, amount, voucher_id, vouchers!inner(project_id, status)")
          .eq("vouchers.project_id", projectId!),
        supabase.from("time_entries")
          .select("id, project_id, user_id, task_description, duration_seconds, budgeted_hours, start_date, status, comment, budget_line_id, assignment_id, users(full_name, avatar_url)")
          .eq("project_id", projectId!)
          .is("deleted_at", null)
          .order("task_description"),
        supabase.from("budget_lines")
          .select("id, project_id, phase, description, quantity, unit, quantity_hours, amount, cost_type, estimated_cost, template_id, upsell_id, is_optional, option_label, is_converted_from_cost, is_locked, sort_order, offer_line_templates(name), budget_line_assignments(id, user_id, hours, label, task_status, completed_at, approved_at, rejection_reason, estimated_deadline, notes, users!budget_line_assignments_user_id_fkey(full_name, avatar_url))")
          .eq("project_id", projectId!)
          // Stable order: phase first, then the offer's own order (sort_order,
          // VIB-2866; lines without one sort last), then creation order, then
          // id as a final tiebreak. Batch inserts give every line the same
          // created_at, so without sort_order the intra-phase order was the
          // uuid, which is random ("postene kommer ikke i opprinnelig
          // rekkefølge"). Without the secondary keys Postgres returns an
          // arbitrary order that reshuffles on every edit.
          .order("phase")
          .order("sort_order", { nullsFirst: false })
          .order("created_at")
          .order("id"),
        supabase.from("project_offer_metadata")
          .select("overhead_pct, discount_pct, discount_amount, offer_total, startup_invoice_pct, final_invoice_pct, include_travel_in_overhead")
          .eq("project_id", projectId!)
          .maybeSingle(),
      ]);

      if (projectRes.error) throw projectRes.error;
      if (budgetRes.error) throw budgetRes.error;

      type OwnerRow = {
        user_id: string;
        ownership_pct: number | null;
        split_type: string | null;
        fixed_amount: number | string | null;
        sales_unit_id: string | null;
        users: { full_name: string; avatar_url: string | null } | null;
      };
      type TimeEntryRow = TimeEntry & { users: { full_name: string; avatar_url: string | null } | null };
      type RawAssignmentRow = {
        id: string;
        user_id: string;
        hours: number;
        label: string | null;
        task_status: string | null;
        completed_at: string | null;
        approved_at: string | null;
        rejection_reason: string | null;
        estimated_deadline: string | null;
        notes: string | null;
        users: { full_name: string; avatar_url: string | null } | null;
      };
      type RawBudgetLineRow = Omit<BudgetLineRow, "template_name" | "assignments"> & {
        offer_line_templates: { name: string } | null;
        budget_line_assignments: RawAssignmentRow[] | null;
      };
      type RawProject = DBProject & { customers: CustomerInfo | null };

      const owners: ProjectOwnerWithUser[] = ((ownersRes.data ?? []) as unknown as OwnerRow[]).map((o) => ({
        user_id: o.user_id,
        ownership_pct: o.ownership_pct,
        split_type: (o.split_type ?? "percent") as SplitType,
        fixed_amount: o.fixed_amount != null ? Number(o.fixed_amount) : null,
        sales_unit_id: o.sales_unit_id ?? null,
        user: o.users ?? null,
      }));

      type RawAllocationRow = {
        budget_line_id: string | null;
        amount: number | string | null;
        voucher_id: string;
        vouchers: { project_id: string; status: string | null } | null;
      };

      const voucherAllocations: VoucherAllocationRow[] = ((allocationsRes.data ?? []) as unknown as RawAllocationRow[]).map((a) => ({
        budget_line_id: a.budget_line_id,
        amount: Number(a.amount ?? 0),
        voucher_id: a.voucher_id,
        voucher_status: a.vouchers?.status ?? null,
      }));

      const timeEntries: TimeEntry[] = ((timeRes.data ?? []) as unknown as TimeEntryRow[]).map((t) => ({
        ...t,
        assignment_id: t.assignment_id ?? null,
        user: t.users ?? null,
      }));

      const budgetLines: BudgetLineRow[] = ((budgetRes.data ?? []) as unknown as RawBudgetLineRow[]).map((bl) => ({
        ...bl,
        template_name: bl.offer_line_templates?.name ?? null,
        assignments: (bl.budget_line_assignments ?? []).map((a) => ({
          id: a.id,
          user_id: a.user_id,
          hours: a.hours,
          label: a.label ?? null,  // VIB-671
          task_status: a.task_status ?? "not_started",
          completed_at: a.completed_at,
          approved_at: a.approved_at,
          rejection_reason: a.rejection_reason,
          estimated_deadline: a.estimated_deadline ?? null,
          notes: a.notes ?? null,
          user: a.users ?? null,
        })),
      }));

      const rawProject = projectRes.data as unknown as RawProject;
      const customer: CustomerInfo | null = rawProject.customers ?? null;

      const offerMetadata: OfferMetadata | null = offerMetaRes.data ? {
        overhead_pct: Number(offerMetaRes.data.overhead_pct ?? 0),
        discount_pct: Number(offerMetaRes.data.discount_pct ?? 0),
        discount_amount: Number(offerMetaRes.data.discount_amount ?? 0),
        offer_total: Number(offerMetaRes.data.offer_total ?? 0),
        startup_invoice_pct: offerMetaRes.data.startup_invoice_pct ? Number(offerMetaRes.data.startup_invoice_pct) : null,
        final_invoice_pct: offerMetaRes.data.final_invoice_pct ? Number(offerMetaRes.data.final_invoice_pct) : null,
        include_travel_in_overhead: offerMetaRes.data.include_travel_in_overhead === true,
      } : null;

      return {
        project: rawProject as DBProject,
        customer,
        owners,
        invoices: invoicesRes.data ?? [],
        vouchers: (vouchersRes.data ?? []) as DBVoucher[],
        voucherAllocations,
        timeEntries,
        budgetLines,
        offerMetadata,
      };
    },
  });
}
